import { useContext, useMemo } from 'react';
import { AdminDashboardPieChartDataType } from './AdminDashboardPieChart.types';
import { getRandomColorForEachElement } from 'basics/utils/getRandomColors';
import { ProductsContext } from 'contexts/products';

const MIN_PERCENT = 0.03;
const RADIAN = Math.PI / 180;

export const useAdminDashboardPieChart = (
  pieData: AdminDashboardPieChartDataType[],
  isProductGrouping: boolean,
) => {
  const { productList } = useContext(ProductsContext);

  const resolveProductName = (externalProductName: string): string => productList
    .find((p) => p.productExternalID === externalProductName)?.productName || externalProductName;

  const transformedPieData = useMemo(() => {
    return pieData.map(({ categoryName, count }) => ({
      categoryName: isProductGrouping ? resolveProductName(categoryName) : categoryName,
      count,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieData, isProductGrouping, productList]);

  const colors = useMemo(() => {
    return getRandomColorForEachElement(pieData);
  }, [pieData]);

  const total = transformedPieData.reduce((sum, item) => sum + item.count, 0);
  const majorItems = transformedPieData.filter((item) => item.count / total >= MIN_PERCENT);
  const otherItems = transformedPieData.filter((item) => item.count / total < MIN_PERCENT);
  const otherTotal = otherItems.reduce((sum, item) => sum + item.count, 0);
  const otherItemsWithPercent = otherItems.map((otherItem) => {
    return {
      ...otherItem,
      percent: ((otherItem.count / total) * 100).toFixed(0),
    };
  });

  const processedData = [
    ...majorItems,
    ...(otherTotal > 0 ? [{ categoryName: 'Other', count: otherTotal, children: otherItemsWithPercent }] : []),
  ];

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const renderLabel = (props: any) => {
    const { cx, cy, midAngle, outerRadius, index, percent } = props;
    const data = processedData[index];
    if (!data) return null;

    const { categoryName, count } = data;
    const radius = outerRadius + 30;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return {
      categoryName,
      count,
      x,
      y,
      percent,
      cx,
    };
  };

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const customTooltip = (props: any) => {
    const { active, payload } = props;
    if (active && payload?.length) {
      const data = payload[0].payload as {
        categoryName: string;
        count: number;
        children?: { categoryName: string; count: number; percent: number }[];
      };
      return {
        ...data,
        percent: ((data.count / total) * 100).toFixed(0),
      };
    }
    return null;
  };

  return {
    processedData,
    colors,
    renderLabel,
    customTooltip,
  };
};
